import { Component, Input, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-audio-player',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './audio-player.component.html',
  styleUrl: './audio-player.component.scss'
})
export class AudioPlayerComponent implements OnDestroy {
  @Input() audioUrl: string = '';
  @ViewChild('audioElement') audioElement!: ElementRef<HTMLAudioElement>;

  isPlaying = false;
  currentTime = 0;
  duration = 0;
  volume = 1;
  isMuted = false;
  isLoading = true;

  private updateInterval: any;

  ngOnDestroy(): void {
    this.clearUpdateInterval();
  }

  onLoadedMetadata(): void {
    const audio = this.audioElement.nativeElement;
    this.duration = audio.duration;
    this.isLoading = false;
  }

  togglePlay(): void {
    const audio = this.audioElement.nativeElement;
    
    if (this.isPlaying) {
      audio.pause();
      this.clearUpdateInterval();
    } else {
      audio.play();
      this.startUpdateInterval();
    }
    
    this.isPlaying = !this.isPlaying;
  }

  onTimeUpdate(): void {
    const audio = this.audioElement.nativeElement;
    this.currentTime = audio.currentTime;
  }

  onEnded(): void {
    this.isPlaying = false;
    this.currentTime = 0;
    this.clearUpdateInterval();
  }

  seek(event: MouseEvent): void {
    const progressBar = event.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    const audio = this.audioElement.nativeElement;
    audio.currentTime = percent * this.duration;
  }

  changeVolume(event: MouseEvent): void {
    const volumeBar = event.currentTarget as HTMLElement;
    const rect = volumeBar.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    this.volume = Math.max(0, Math.min(1, percent));
    const audio = this.audioElement.nativeElement;
    audio.volume = this.volume;
    
    if (this.volume > 0 && this.isMuted) {
      this.isMuted = false;
    }
  }

  toggleMute(): void {
    const audio = this.audioElement.nativeElement;
    this.isMuted = !this.isMuted;
    audio.muted = this.isMuted;
  }

  skipBackward(): void {
    const audio = this.audioElement.nativeElement;
    audio.currentTime = Math.max(0, audio.currentTime - 10);
  }

  skipForward(): void {
    const audio = this.audioElement.nativeElement;
    audio.currentTime = Math.min(this.duration, audio.currentTime + 10);
  }

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) {
      return '0:00';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  get progressPercent(): number {
    return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
  }

  get volumePercent(): number {
    return this.isMuted ? 0 : this.volume * 100;
  }

  private startUpdateInterval(): void {
    this.updateInterval = setInterval(() => {
      this.onTimeUpdate();
    }, 100);
  }

  private clearUpdateInterval(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }
}
